import { useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { Button, IconButton } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import Icon from 'components/icons/Icon';
import ParkingSignInButton from 'components/park/ParkingSignInButton';
import {
  FooterButtonContainer,
  VerticalDistributedContainer,
} from 'components/sheets/SheetLayout';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import ParkingContext from 'context/ParkingContext';
import { differenceInMinutes, startOfMinute } from 'date-fns';
import useAddPaymentMethod from 'hooks/useAddPaymentMethod';
import useCreateSession from 'hooks/useCreateSession';
import useExternalPaymentMethods from 'hooks/useExternalPaymentMethods';
import useGetPoolingInfo from 'hooks/useGetPoolingInfo';
import useHasParkingSession from 'hooks/useHasParkingSession';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useSelectedCompanyBenefit from 'hooks/useSelectedCompanyBenefit';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import { SelectedPaymentMethod } from 'hooks/useSelectedPaymentMethod';
import { groupBy, isEmpty } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking } from 'react-native';
import { parkingPaymentCardId } from 'states/common';
import {
  country,
  featureCountryCode,
  onboardingCompleted,
} from 'states/persistInStorage';
import { useAuthStore } from 'stores/authStore';
import { useMapStore } from 'stores/mapStore';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { usePaymentStore } from 'stores/paymentStore';
import { useUiStore } from 'stores/uiStore';
import styled from 'styled-components/native';
import { Vehicle } from 'types/generatedSchemaTypes';
import { ParkNavProps, RootNavProps } from 'types/navigation';
import {
  isMobilePayEnabledForCountry,
  showFeatureFlag,
} from 'utils/featureFlagUtils';
import {
  getMissingPaymentTextKey,
  getMissingVehicleTextKey,
  hasValidParkingDuration,
} from 'utils/parkingButtonValidation';
import { arePaymentMethodsEqual, isExternalPayment } from 'utils/paymentUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { getScheduleFromDate, minutesToHoursAndMinutes } from 'utils/timeUtils';
import PoolingFullInfo from '../pooling/PoolingFullInfo';
import PreloadingWrapper from '../timeSelection/PreloadingWrapper';
import PaymentCardDropDown from './PaymentCardDropDown';
import VehicleSelectDropDown from './VehicleSelectDropDown';

type DefineDisabledProps = {
  parkingZoneUid: string | undefined;
  allowedTimes:
    | {
        parkingAllowedFrom: string;
        parkingAllowedUntil: string;
      }
    | undefined;
  isValidEndTime: boolean | undefined;
};

const IconButtonWrapper = styled.View<{ marginBottom?: number }>`
  flex: 0 1 auto;
  margin-bottom: ${(p) => p.marginBottom ?? 0}px;
`;

const PrimaryButtonWrapper = styled.View<{
  marginLeft?: number;
  marginBottom?: number;
}>`
  flex: 1 1 auto;
  margin-left: ${(p) => p.marginLeft ?? 0}px;
  margin-bottom: ${(p) => p.marginBottom ?? 0}px;
`;

export const defineDisabled = ({
  parkingZoneUid,
  allowedTimes,
  isValidEndTime,
}: DefineDisabledProps) => {
  return (
    !parkingZoneUid || !allowedTimes?.parkingAllowedFrom || !isValidEndTime
  );
};

const CalendarIcon: React.FC = () => <Icon name="Calendar-Outlined" size="l" />;

const StartParkingButton = ({
  setCustomEndTime = false,
  ...props
}: {
  setCustomEndTime?: boolean;
}) => {
  const { navigate } = useNavigation<RootNavProps>();
  const { navigate: navigatePark } = useNavigation<ParkNavProps>();
  const { t } = useTranslation();
  const { parkingZoneResult } = useContext(ParkingContext) || {};

  const {
    parkingConditions,
    selectedParkingVehiclePlate,
    currentPaymentMethodId,
    selectedParkingSegment,
    setParkingConditions,
    setCurrentPaymentMethodId,
    setParkingVehiclePlate,
  } = useParkingStore((state) => state);
  const {
    parkingZoneCountryCode,
    selectedParkingZoneId,
    endTime,
    isValidEndTime,
    paymentMethod,
  } = parkingConditions;
  const selectedVehicle = useSelectedParkingVehicle(
    false,
    undefined,
    selectedParkingVehiclePlate
  );
  const {
    customer: { vehicles, paymentOptions, contactEmail },
  } = useContext(AccountContext);
  const { isLoggedIn, loginLoading } = useIsLoggedIn();
  const { companyPays: permitPaidByCompany, poolingGroup } = useGetPoolingInfo(
    selectedParkingZoneId
  );
  const allowedTimes = {
    parkingAllowedFrom: selectedParkingSegment?.parkingAllowedFrom || '',
    parkingAllowedUntil: selectedParkingSegment?.parkingAllowedUntil || '',
  };
  const noPaymentMethodAndCompanyWontPay =
    isEmpty(paymentMethod) && !permitPaidByCompany;

  const schedule =
    parkingZoneResult?.data?.parkZone?.parkingFacility?.timeSlots;
  const unmanagedParking =
    parkingZoneResult?.data?.parkZone?.parkingMethods?.unmanagedParking;
  const groupedSchedule = groupBy(schedule, 'weekDay');
  const countryCode = useReactiveVar(country);
  const {
    flags: { anpr, park_schedule, enable_mobilepay_by_country },
  } = useContext(FeatureFlagsContext);
  const showCameraRecognition = showFeatureFlag(anpr);
  const selectedTariff = useParkzoneStore((state) => state.selectedTariffId);
  const externalPaymentLoading = usePaymentStore(
    (s) => s.externalPaymentFlowLoading
  );
  const appCountryCode = useReactiveVar(featureCountryCode);
  const showParkSchedule = showFeatureFlag(park_schedule);
  const showMobilePay = isMobilePayEnabledForCountry(
    enable_mobilepay_by_country,
    appCountryCode,
    contactEmail
  );
  const closedZoneUid = useMapStore((state) => state.canStartParkingZoneUid);
  const isUserRestricted = useAuthStore((state) => state.accountRestricted);
  const resumableSessionInfo = useParkingStore(
    (state) => state.resumableSession
  );
  const canResumeSession = useParkingStore(
    (state) => state.resumeSessionPossible
  );
  const hasVehicles = !!(vehicles && vehicles.length > 0);
  const { addCard, loadingAddCard } = useAddPaymentMethod();
  const externalPayment = isExternalPayment(paymentMethod);
  const { externalPaymentMethods } = useExternalPaymentMethods();
  const { companyBenefit } = useSelectedCompanyBenefit();
  const hasOtherPaymentMethods =
    !permitPaidByCompany &&
    ((externalPaymentMethods?.length ?? 0) > 0 ||
      (paymentOptions?.length ?? 0) > 0) &&
    isEmpty(paymentMethod);

  const hasMobilePay = paymentOptions?.some(
    (option) => option?.method === 'MOBILEPAY'
  );
  // Only a selected corporate account can mismatch the zone's country; with no
  // benefit selected there is nothing to drop.
  const companyBenefitCountryMismatch =
    !!companyBenefit && companyBenefit.countryCode !== parkingZoneCountryCode;

  const {
    createParking,
    createParkingLoading: startParkingLoading,
    parkingSuccess,
  } = useCreateSession({});

  const clearExternalPaymentMethod = () => {
    parkingPaymentCardId(undefined);
    setCurrentPaymentMethodId(undefined);
    return showToast(
      t('parking.parkingProcess.externalAppOpenFailed', {
        provider: paymentMethod?.externalPaymentType,
      }),
      'error'
    );
  };

  const setVehicleState = useCallback(
    (vehicle?: Vehicle) => {
      // Only update if the vehicle actually changed
      if (vehicle?.licensePlate?.plateText !== selectedParkingVehiclePlate) {
        setParkingVehiclePlate(vehicle?.licensePlate?.plateText);
      }
    },
    [selectedParkingVehiclePlate, setParkingVehiclePlate]
  );

  const setCardState = useCallback(
    (selectedPaymentMethod?: SelectedPaymentMethod) => {
      if (!arePaymentMethodsEqual(selectedPaymentMethod, paymentMethod)) {
        setParkingConditions({ paymentMethod: selectedPaymentMethod });
      }
    },
    [setParkingConditions, paymentMethod]
  );

  const requiresCameraRecognitionCheck =
    showCameraRecognition &&
    !!parkingZoneResult?.data?.parkZone?.parkingMethods?.anpr &&
    !selectedVehicle?.cameraRecognition?.enabled;

  const isIncompleteOnboarding =
    noPaymentMethodAndCompanyWontPay &&
    !selectedVehicle &&
    !onboardingCompleted();

  const handleMissingPaymentMethod = () => {
    if (hasOtherPaymentMethods) {
      useUiStore.getState().setSheetModal('parkingPaymentMethod');
      return;
    }
    if (showMobilePay && !hasMobilePay) {
      useUiStore.getState().setSheetModal('addOtherPaymentMethod');
      return;
    }
    addCard();
  };

  const handleMissingVehicle = () => {
    if (hasVehicles) {
      useUiStore.getState().setSheetModal('parkingVehicle');
    } else {
      navigate('addVehicleParking');
    }
  };

  const handleStartParking = async () => {
    if (isUserRestricted) {
      useUiStore.getState().setSheetModal('accountRestricted');
      return;
    }
    if (requiresCameraRecognitionCheck) {
      useUiStore.getState().setSheetModal('cameraRecognitionStartParking');
      return;
    }
    if (isIncompleteOnboarding) {
      navigate('onboardingModal', { screen: 'welcome' });
      return;
    }
    if (noPaymentMethodAndCompanyWontPay) {
      handleMissingPaymentMethod();
      return;
    }
    if (!selectedVehicle) {
      handleMissingVehicle();
      return;
    }
    if (!endTime) {
      setParkingConditions({ endTime: startOfMinute(new Date()) });
      navigatePark('parkEndTime', { parkingZoneUid: selectedParkingZoneId });
      return;
    }
    if (resumableSessionInfo?.sessionId && !canResumeSession) {
      navigatePark('parkResume', { parkingZoneUid: selectedParkingZoneId });
      return;
    }
    if (!isOpen && closedZoneUid !== selectedParkingZoneId) {
      useUiStore.getState().setSheetModal('confirmStartParking');
      return;
    }
    if (externalPayment) {
      // Currently we only have 1 external payment method. This linking needs to be changed to
      // target more payment app URLs in the future, so a function can be created for this.
      const canOpenExternalApp = await Linking.canOpenURL('swish://');
      canOpenExternalApp ? await createParking() : clearExternalPaymentMethod();
      return;
    }
    await createParking();
  };

  useEffect(() => {
    if (companyBenefitCountryMismatch) {
      parkingPaymentCardId(undefined);
      setCurrentPaymentMethodId(undefined);
    }
  }, [companyBenefitCountryMismatch, setCurrentPaymentMethodId]);

  useEffect(() => {
    if (selectedParkingZoneId && selectedParkingZoneId === closedZoneUid) {
      useMapStore.getState().setCanStartParkingZoneUid('');
      createParking();
    }
    return () => {
      useMapStore.getState().setCanStartParkingZoneUid('');
    };
  }, [closedZoneUid, createParking, selectedParkingZoneId]);

  const { isOpen } = getScheduleFromDate(
    groupedSchedule,
    new Date(),
    countryCode
  );

  const minuteDif = useMemo(() => {
    if (endTime) {
      return differenceInMinutes(endTime, new Date()) + 1;
    }
  }, [endTime]);

  const missingDetails = useMemo(
    () => noPaymentMethodAndCompanyWontPay || !selectedVehicle,
    [noPaymentMethodAndCompanyWontPay, selectedVehicle]
  );

  const { hasParkingSession } = useHasParkingSession({
    parkingZoneUid: selectedParkingZoneId,
    vehicle: selectedVehicle,
  });

  const disabled = missingDetails
    ? false
    : setCustomEndTime ||
      startParkingLoading ||
      externalPaymentLoading ||
      defineDisabled({
        parkingZoneUid: selectedParkingZoneId,
        allowedTimes,
        isValidEndTime: isValidEndTime,
      }) ||
      !!unmanagedParking ||
      (!!minuteDif && minuteDif < 2) ||
      hasParkingSession;

  const minutesAndHours = useMemo(() => {
    if (minuteDif) {
      return minutesToHoursAndMinutes(minuteDif);
    }
  }, [minuteDif]);

  if (!isLoggedIn) {
    return (
      <ParkingSignInButton
        testID="park-zone-details-login"
        loading={loginLoading}
      />
    );
  }

  const buttonText = () => {
    // Priority 1-2: Check for missing payment
    const paymentTextKey = getMissingPaymentTextKey(
      hasOtherPaymentMethods,
      noPaymentMethodAndCompanyWontPay
    );
    if (paymentTextKey) {
      return t(paymentTextKey);
    }

    // Priority 3-4: Check for missing vehicle
    const vehicleTextKey = getMissingVehicleTextKey(
      selectedVehicle,
      hasVehicles
    );
    if (vehicleTextKey) {
      return t(vehicleTextKey);
    }

    // Priority 5: No tariff selected
    if (!selectedTariff && !parkingSuccess && !poolingGroup) {
      return t('parking.tariff.selectTariff');
    }

    // Priority 6: Button disabled due to missing/invalid end time
    if (disabled && (!minutesAndHours || !hasValidParkingDuration(minuteDif))) {
      return t('parking.parkingProcess.selectEndTime');
    }

    // Priority 7: No parking time selected
    if (!minutesAndHours) {
      return t('parking.parkingProcess.selectParkingTime');
    }

    // Priority 8: Loading state
    if (startParkingLoading || externalPaymentLoading) {
      return t('parking.parkingProcess.startParkingLoading');
    }

    // Priority 9: Button enabled with valid time
    if (minutesAndHours && !disabled && hasValidParkingDuration(minuteDif)) {
      return resumableSessionInfo?.startDate
        ? t('parking.parkingProcess.resumeParkingButtonLabel', {
            time: minutesAndHours,
          })
        : t('parking.parkingProcess.startParkingButtonLabel', {
            time: minutesAndHours,
          });
    }

    // Priority 10: Default disabled state
    return t('parking.parkingProcess.startParkingButtonNoDuration');
  };

  return (
    <FooterButtonContainer {...props} pb={0}>
      <VerticalDistributedContainer>
        <PoolingFullInfo parkingZoneUid={selectedParkingZoneId} />
        <FooterButtonContainer pt={0} pb={0} prPl={10}>
          <VehicleSelectDropDown
            currentVehiclePlate={selectedParkingVehiclePlate}
            setVehicleState={setVehicleState}
          />
          <PaymentCardDropDown
            parkingZoneUid={selectedParkingZoneId}
            parkingZoneCountryCode={parkingZoneCountryCode}
            currentPaymentMethodId={currentPaymentMethodId}
            setCardState={setCardState}
          />
        </FooterButtonContainer>
        <FooterButtonContainer>
          {showParkSchedule && (
            <IconButtonWrapper>
              <IconButton
                fullWidth={false}
                buttonType="tertiary"
                testID="park-zone-details-calendar-button"
                iconComponent={CalendarIcon}
                accessibilityLabel={t('parking.closedZoneConfirm')}
              />
            </IconButtonWrapper>
          )}
          <PrimaryButtonWrapper marginLeft={showParkSchedule ? 15 : 0}>
            <PreloadingWrapper
              opacitySecondItem
              firstItem={
                <Button
                  buttonType={missingDetails ? 'secondary' : 'primary'}
                  size="l"
                  text={buttonText()}
                  {...testIdentifiers('park-zone-details-start-parking')}
                  onPress={handleStartParking}
                  disabled={disabled}
                  loading={
                    startParkingLoading ||
                    loadingAddCard ||
                    externalPaymentLoading
                  }
                />
              }
              secondItem={
                <Button
                  size="l"
                  buttonType={missingDetails ? 'secondary' : 'primary'}
                  text={buttonText()}
                  loading
                  disabled={disabled}
                />
              }
            />
          </PrimaryButtonWrapper>
        </FooterButtonContainer>
      </VerticalDistributedContainer>
    </FooterButtonContainer>
  );
};

export default StartParkingButton;
